import { useEffect, useState } from 'react';
import './custom-product-pricing-config.scss';
import { CustomProductPricingConfig } from '../../../clients/quipli-service/pricing/pricing';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DataTable } from '../../../components/table/data-table';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { CustomProductPricingConfigModal } from './custom-product-pricing-config-modal';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';

type CustomProductPricingConfigTableRowData = {
  duration: string;
  actions: string | JSX.Element;
};

export const CustomProductPricingConfigTable = () => {
  const { listCustomProductPricingConfigs, deleteCustomProductPricingConfig } = usePricingApiV1();

  const [pricingConfigs, setPricingConfigs] = useState<CustomProductPricingConfig[]>([]);
  const [pricingConfigRowData, setPricingConfigRowData] = useState<CustomProductPricingConfigTableRowData[]>([]);
  const [selectedPricingConfig, setSelectedPricingConfig] = useState<CustomProductPricingConfig | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const columnNames = [
    { key: 'duration', label: 'Allow Pricing Per' },
    { key: 'actions', label: ' ' },
  ];

  const loadPricingConfigs = async () => {
    setIsLoading(true);

    listCustomProductPricingConfigs()
      .then(setPricingConfigs)
      .catch(() => toast('Error loading pricing configs', 'error'))
      .finally(() => setIsLoading(false));
  };

  const deleteConfig = (id: string) => {
    deleteCustomProductPricingConfig(id)
      .then(() => {
        loadPricingConfigs();
        toast('Pricing config deleted successfully', 'success');
      })
      .catch(() => {
        toast('Error deleting pricing config', 'error');
      });
  };

  useEffect(() => {
    loadPricingConfigs();
  }, []);

  useEffect(() => {
    const rowData = pricingConfigs.map((config) => {
      return {
        duration: `${config.durationValue} ${config.durationUnit}${config.durationValue > 1 ? 's' : ''}`,
        actions: (
          <div className="flex justify-end">
            <i
              className="fa-solid fa-pencil mr-3 cursor-pointer"
              onClick={() => {
                setSelectedPricingConfig(config);
                setIsModalOpen(true);
              }}
            />
            <i className="fa-solid fa-trash-can cursor-pointer" onClick={() => deleteConfig(config.id)} />
          </div>
        ),
      };
    });

    setPricingConfigRowData(rowData);
  }, [pricingConfigs]);

  return (
    <div className="price-config-section">
      {isLoading ? (
        <tr>
          <td colSpan={100}>
            <Spinner />
          </td>
        </tr>
      ) : (
        <>
          <div className="mb-3 flex items-end justify-between">
            <Button
              className="button-primary ml-auto mr-4 cursor-pointer rounded border border-solid border-gray-300 bg-white px-2 py-1 text-sm text-gray-500 shadow-sm hover:bg-gray-200"
              onClick={() => {
                setSelectedPricingConfig(undefined);
                setIsModalOpen(true);
              }}
            >
              <span className="flex items-center">
                <Icon icon="Plus" className="pr-2" stroke="white" />
                Add Pricing Duration
              </span>
            </Button>
          </div>
          <DataTable
            columnNames={columnNames}
            className="pricing-config-table"
            data={pricingConfigRowData.map((d) => ({ mobileHeader: null, fields: d }))}
            showAlwaysDesktop={true}
            noDataText="No custom product pricing durations yet"
          />

          <CustomProductPricingConfigModal
            isOpen={isModalOpen}
            config={selectedPricingConfig}
            close={async (refresh: boolean = false) => {
              setSelectedPricingConfig(undefined);
              await (refresh ? loadPricingConfigs() : Promise.resolve());
              setIsModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};
